import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Tab, Tabs, Typography } from '@mui/material';
import { AuthService } from '../../../services/auth.service';
import { AccountService } from '../../../services/account.service';
import { CorporateTaxService } from '../../../services/corporateTax.service';
import { Account } from '../../../interfaces/accounting.interface';
import { CorporateTaxSettings } from '../../../interfaces/corporateTax.interface';
import { useToast } from '../../../context/ToastContext';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import LedgerTab from './LedgerTab';
import ComputationTab from './ComputationTab';
import DeferredTab from './DeferredTab';
import PaymentsTab from './PaymentsTab';
import SettingsTab from './SettingsTab';

/**
 * Corporate tax: the AIT / source-tax credit ledger (read from the GL), current-tax computation and provision,
 * simple deferred tax, challan payments and AIT set-off. Rates are data you load and verify; nothing is hard-coded.
 */
const CorporateTaxPage: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const canManage = usePermission(PERMISSIONS.ACCOUNT_MANAGE);
    const canPostJournals = usePermission(PERMISSIONS.JOURNAL_ENTRY_POST);
    const canPost = canManage && canPostJournals;
    const [tab, setTab] = useState(0);
    const [settings, setSettings] = useState<CorporateTaxSettings | null>(null);
    const [accounts, setAccounts] = useState<Account[]>([]);

    const loadSettings = useCallback(async () => {
        try {
            setSettings(await CorporateTaxService.getSettings(institutionId));
        } catch (e) {
            toast.showApiError(e, 'Failed to load corporate tax settings');
        }
    }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { loadSettings(); }, [loadSettings]);
    useEffect(() => {
        (async () => {
            try { setAccounts(await AccountService.getAllAccounts()); }
            catch (e) { toast.showApiError(e, 'Failed to load accounts'); }
        })();
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const startMonth = settings?.fiscalYearStartMonth || 1;
    const notConfigured = !!settings && settings.aitAccounts.length === 0;

    return (
        <Box data-help-page="accounting.corpTax.page" sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h5">Corporate tax</Typography>
            <Typography variant="body2" color="text.secondary">
                Tax deducted at source is read from the ledger lines on your AIT accounts, current tax is computed from the
                year's profit, and the provision, deferred tax, challan payments and set-off post to the ledger.
                Statutory rates are starting points: verify every rate with your tax adviser before posting.
            </Typography>
            {notConfigured && (
                <Alert severity="info">
                    No AIT accounts are configured yet. Open the Settings tab and choose the accounts that hold advance
                    income tax, or use "Detect" to propose them.
                </Alert>
            )}
            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" aria-label="Corporate tax sections">
                <Tab data-help="accounting.corpTax.tabLedger" label="Ledger" />
                <Tab data-help="accounting.corpTax.tabComputation" label="Computation" />
                <Tab data-help="accounting.corpTax.tabDeferred" label="Deferred tax" />
                <Tab data-help="accounting.corpTax.tabPayments" label="Payments" />
                <Tab data-help="accounting.corpTax.tabSettings" label="Settings" />
            </Tabs>
            {tab === 0 && <LedgerTab institutionId={institutionId} startMonth={startMonth} canManage={canManage} canPost={canPost} />}
            {tab === 1 && <ComputationTab institutionId={institutionId} startMonth={startMonth} settings={settings} canManage={canManage} canPost={canPost} />}
            {tab === 2 && <DeferredTab institutionId={institutionId} startMonth={startMonth} canManage={canManage} canPost={canPost} />}
            {tab === 3 && <PaymentsTab institutionId={institutionId} settings={settings} accounts={accounts} canPost={canPost} />}
            {tab === 4 && (
                <SettingsTab institutionId={institutionId} settings={settings} accounts={accounts} canManage={canManage}
                    onSaved={loadSettings} />
            )}
        </Box>
    );
};

export default CorporateTaxPage;
